import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import useFetch from "../../../../hooks/useFetch";

const SOFT_SESSION_LIMIT_SEC = 5 * 60; // 5 minutes — gentle nudge only, never a hard stop
const QUESTION_TIME_SEC = 20; // preschoolers get more breathing room than the IQ game

const THEME_BY_WEEKDAY = ["Family", "Animals", "Fruits", "HabitsAndTouches", "Colours", "Numbers", "Vehicles"];
// const THEME_BY_WEEKDAY = ["Family", "Animals", "Fruits", "Vehicles", "Colours", "Numbers", "HabitsAndTouches"];

function todaysTheme() {
  return THEME_BY_WEEKDAY[new Date().getDay()];
}

export function useTinyThinkGame({ userId, userCode, userName }) {
  const authState = useSelector((state) => state.authReducer || {});
  const [fetchData] = useFetch();

  const user = authState._id || null;
  const token = authState.token;

  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [questionsError, setQuestionsError] = useState(null);

  const [theme, setTheme] = useState(todaysTheme());
  const [currentLevel, setCurrentLevel] = useState(1);
  const [phase, setPhase] = useState('home'); // home | playing | levelResult | dailySummary

  const [currentQ, setCurrentQ] = useState(0);
  const [answered, setAnswered] = useState(false);
  const [selectedOpt, setSelectedOpt] = useState(null);
  const [timeLeft, setTimeLeft] = useState(QUESTION_TIME_SEC);
  const [feedback, setFeedback] = useState(null);

  const [history, setHistory] = useState([]); // cached, fetched once
  const [todayCompleted, setTodayCompleted] = useState(false);
  const [streakInfo, setStreakInfo] = useState(null);
  const [bestRecord, setBestRecord] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]); // cached, fetched once

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState({ text: "", type: "" });

  const [lastLevelResult, setLastLevelResult] = useState(null);
  const [surpriseBox, setSurpriseBox] = useState(null);

  const [sessionElapsedSec, setSessionElapsedSec] = useState(0);
  const [showTimeNudge, setShowTimeNudge] = useState(false);

  const timerRef = useRef(null);
  const qStartRef = useRef(null);
  const feedbackTimeoutRef = useRef(null);
  const sessionTimerRef = useRef(null);
  const levelStartTimeRef = useRef(0);
  const levelDataRef = useRef({ correct: 0, hintsUsed: 0, answerTimes: [] });
  const fetchedOnceRef = useRef(false);

  const TOTAL = questions?.length || 0;

  // ── Fetch Questions ─────────────────────────────
  const fetchQuestions = async (level, category) => {
    try {
      setLoadingQuestions(true);
      setQuestionsError(null);

      const config = {
        url: "/tinythink/questions",
        method: "post",
        data: { level, theme: category },
        headers: { Authorization: token },
      };
      const response = await fetchData(config, { silent: true });
      const questionsList = response?.questions || response;
      if (Array.isArray(questionsList)) {
        const randomized = randomizeQuestions(questionsList);
        setQuestions(randomized);
      } else {
        setQuestionsError("Invalid questions data");
      }
    } catch (error) {
      console.error("TinyThink Questions Fetch Error:", error);
      setQuestionsError("Failed to load questions");
    } finally {
      setLoadingQuestions(false);
    }
  };

  const shuffleArray = (arr) => {
    const copy = [...arr];

    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }

    return copy;
  };

  const randomizeQuestions = (list) => {
    return shuffleArray(list).map((q) => ({
      ...q,
      options: shuffleArray(q.options || []),
    }));
  };

  // NEW: matches backend's YYYY-MM-DD IST format so the comparison lines up exactly
const getTodayIST = () => {
  const now = new Date();
  const istOffsetMs = 5.5 * 60 * 60 * 1000;
  const istDate = new Date(now.getTime() + istOffsetMs + now.getTimezoneOffset() * 60 * 1000);
  return istDate.toISOString().slice(0, 10);
};

const fetchHistory = async () => {
    try {
      const config = {
        url: "/tinythink/history",
        method: "post",
        data: { userId },
        headers: { Authorization: token },
      };

      const response = await fetchData(config, { silent: true });
      if (response?.success) {
        const historyList = response.history || [];
        setHistory(historyList);
        if (response.streak) {
          setStreakInfo((prev) => ({ ...prev, ...response.streak }));
        }
        if (response.bestRecord) {
          setBestRecord(response.bestRecord);
        }
        // NEW: check if today's entry exists and level 5 has been reached
        // (levelScores[4] !== null means level 5 was played, regardless of its score value)
        const today = getTodayIST();
        const todaysEntry = historyList.find((h) => h.playDate === today);
        const alreadyCompletedToday =
          !!todaysEntry && todaysEntry.levelScores?.[4] !== null && todaysEntry.levelScores?.[4] !== undefined;

        setTodayCompleted(alreadyCompletedToday); // NEW state, see below
      }
    } catch (e) {
      console.error("TinyThink Progress Fetch Error:", e);
      setHistory([]);
    }
  };

  useEffect(() => {
    if (!userId || fetchedOnceRef.current) return;
    fetchedOnceRef.current = true;
    fetchHistory();
  }, [userId]);

  const fetchLeaderboard = async () => {
    try {
      const config = {
        url: "/tinythink/leaderboard",
        method: "post",
        headers: {
          Authorization: token,
        },
      };

      const response = await fetchData(config, { silent: true });

      if (response?.success) {
        setLeaderboard(response.leaderboard || []);
      } else {
        setLeaderboard([]);
      }

      return response;
    } catch (err) {
      console.error(err);

      setLeaderboard([]);

      return {
        success: false,
        leaderboard: [],
      };
    }
  };

  // ── Soft 5-minute session timer (never blocks, only nudges) ────
  useEffect(() => {
    if (phase === "home") return;
    sessionTimerRef.current = setInterval(() => {
      setSessionElapsedSec((prev) => {
        const next = prev + 1;
        if (next === SOFT_SESSION_LIMIT_SEC) setShowTimeNudge(true);
        return next;
      });
    }, 1000);
    return () => clearInterval(sessionTimerRef.current);
  }, [phase]);

  const dismissTimeNudge = useCallback(() => setShowTimeNudge(false), []);

  const clearTimer = () => clearInterval(timerRef.current);

  const startTimer = (q) => {
    clearTimer();

    let t = q.time || QUESTION_TIME_SEC;
    setTimeLeft(t);

    timerRef.current = setInterval(() => {
      t--;
      setTimeLeft(t);

      if (t <= 0) {
        clearTimer();
        handleTimeout(q);
      }
    }, 1000);
  };

  const advanceOrFinish = () => {
    setFeedback(null);

    if (currentQ + 1 >= TOTAL) {
      finishLevel();
    } else {
      setCurrentQ((c) => c + 1);
    }
  };

  const handleTimeout = (q) => {
    if (answered) return;

    setAnswered(true);
    levelDataRef.current.answerTimes.push(q.time || QUESTION_TIME_SEC);

    setFeedback({
      correct: false,
      title: "Almost! ⏰",
      sub: "The answer was: " + q.answer,
      correctAnswer: q.answer,
    });

    feedbackTimeoutRef.current = setTimeout(advanceOrFinish, 1800);
  };

  const loadQuestion = (idx) => {
    if (!questions[idx]) return;

    setAnswered(false);
    setSelectedOpt(null);
    setFeedback(null);

    qStartRef.current = Date.now();

    clearTimer();
    feedbackTimeoutRef.current && clearTimeout(feedbackTimeoutRef.current);
    startTimer(questions[idx]);
  };

  useEffect(() => {
    if (phase !== "playing") return;
    if (!questions[currentQ]) return;

    loadQuestion(currentQ);
  }, [phase, currentQ, questions]);

  const selectAnswer = useCallback((opt) => {
    if (answered || !questions[currentQ]) return;

    clearTimer();
    setAnswered(true);
    setSelectedOpt(opt);

    const q = questions[currentQ];
    const isCorrect = opt === q.answer;
    const elapsed = (Date.now() - qStartRef.current) / 1000;

    levelDataRef.current.answerTimes.push(elapsed);

    if (isCorrect) {
      levelDataRef.current.correct += 1;
    }

    const correctMsgs = [
      ['Yay! 🌟', 'You got it!'],
      ['Super smart! 🧠', 'Great job!'],
      ['Woohoo! 🎉', 'That was perfect!'],
    ];
    const wrongMsgs = [
      ['Nice try! 💛', 'The answer was: ' + q.answer],
      ['So close! 🙂', 'Let\'s try the next one'],
    ];

    const msgs = isCorrect ? correctMsgs : wrongMsgs;
    const [title, sub] = msgs[Math.floor(Math.random() * msgs.length)];

    setFeedback({
      correct: isCorrect,
      title,
      sub,
      correctAnswer: q.answer,
    });

    feedbackTimeoutRef.current = setTimeout(advanceOrFinish, 1800);
  }, [answered, currentQ, questions, TOTAL]);

  // ── Start / Level Flow ─────────────────────────────
  const startSession = useCallback(async () => {
    if (todayCompleted) {
      setMessage({ text: "You've already played today — come back tomorrow!", type: "info" });
      return;
    }
    const level = 1;
    setCurrentLevel(level);

    levelDataRef.current = { correct: 0, hintsUsed: 0, answerTimes: [] };

    await fetchQuestions(level, theme);
    setCurrentQ(0);

    levelStartTimeRef.current = Date.now();
    setAnswered(false);
    setSelectedOpt(null);
    setFeedback(null);
    setLastLevelResult(null);

    setPhase("playing");
  }, [theme]);

  const submitLevelResult = useCallback(
    async ({ level, correctCount, totalQuestions, timeTakenSec, hintsUsed }) => {
      try {
        setLoading(true);

        const config = {
          url: "/tinythink/save-score",
          method: "post",
          data: {
            userId, userCode, userName, level,
            category: theme,
            correctCount, totalQuestions, timeTakenSec, hintsUsed,
          },
          headers: { Authorization: token },
        };

        const res = await fetchData(config, { silent: false });

        if (!res?.success) {
          setMessage({ text: "Could not save score", type: "error" });
          throw new Error("Save failed");
        }

        setLastLevelResult({ ...res.levelResult, ...res.overallRecord, currentLevel: level });
        if (res.surpriseBox) setSurpriseBox(res.surpriseBox);
        if (res.streak) setStreakInfo((prev) => ({ ...prev, ...res.streak }));

        setPhase("levelResult");
        return { ...res.levelResult, ...res.overallRecord };
      } catch (err) {
        console.error("TinyThink Save Score Error:", err);
        setError(err.message);
        setMessage({ text: "Failed to save score", type: "error" });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [theme, userId, userCode, userName, token]
  );

  const finishLevel = async () => {
    if (phase !== "playing") return;
    clearTimer();

    const g = levelDataRef.current;
    const timeTakenSec = Math.round((Date.now() - levelStartTimeRef.current) / 1000);

    try {
      await submitLevelResult({
        level: currentLevel,
        correctCount: g.correct,
        totalQuestions: TOTAL,
        timeTakenSec,
        hintsUsed: g.hintsUsed,
      });
    } catch (e) {
      // error state already set inside submitLevelResult
    }
  };

  const stopSession = () => {
    clearTimer();

    if (feedbackTimeoutRef.current) {
      clearTimeout(feedbackTimeoutRef.current);
      feedbackTimeoutRef.current = null;
    }
  };

  const goToNextLevel = useCallback(async () => {
    if (currentLevel < 5) {
      const next = currentLevel + 1;
      setCurrentLevel(next);

      levelDataRef.current = { correct: 0, hintsUsed: 0, answerTimes: [] };
      await fetchQuestions(next, theme);
      setCurrentQ(0);

      levelStartTimeRef.current = Date.now();
      setAnswered(false);
      setSelectedOpt(null);
      setFeedback(null);
      setLastLevelResult(null);

      setPhase("playing");
    } else {
      setPhase("dailySummary");
    }
  }, [currentLevel, theme]);

  const returnHome = useCallback(() => {
    stopSession();
    setPhase("home");
    setSurpriseBox(null);
    setSessionElapsedSec(0);
    setShowTimeNudge(false);
  }, []);

  useEffect(() => {
    return () => stopSession();
  }, []);

  return {
    theme, setTheme,
    currentLevel, phase,
    currentQ,
    timeLeft,
    timeTotal: questions[currentQ]?.time || QUESTION_TIME_SEC,
    questions,
    question: questions[currentQ],
    answered,
    selectedOpt,
    feedback,
    TOTAL,
    history, streakInfo, todayCompleted, bestRecord, leaderboard,
    loading: loading || loadingQuestions,
    questionsError,
    loadingQuestions,
    message,
    lastLevelResult, surpriseBox,
    sessionElapsedSec, showTimeNudge, dismissTimeNudge,
    startSession, selectAnswer, goToNextLevel, returnHome, stopSession,
    fetchHistory, fetchQuestions, submitLevelResult, fetchLeaderboard
  };
}